import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCars } from '../../../context/CarsContext';
import { config } from '../../../config/runtimeConfig';
import { noteLandingShown, trackLandingEvent } from '../../../services/landingEvents';
import LeadFormEmbed from '../../registry/LeadFormEmbed';
import { AndysBuyRentLease } from './AndysBuyRentLease';
import { ANDYS_DEFAULT_TAB } from './andysLandingTabs';
import { ANDYS_RENTAL_ENGINE_URL } from './andysRentLocations';
import { buildAndysBfcUrl, buildAndysMenuItems } from './andysSiteMenuItems';
import { buildPopularCarSlides } from './andysPopularCarSlides';
import { buildLeaseReserveAttrs } from './leaseReserveAttrs';

// Andys car showroom (витрина) on the dealer site — used when the resolved
// DEALER_CATALOG_URL is unavailable (e.g. the standalone preview) so Buy still
// leaves for andys-motors.com, never the demo domain (#1108).
const ANDYS_SHOWROOM_URL = 'https://andys-motors.com/used-cars';

// Andys leasing page — «Browse all» fallback when the origin-aware
// DEALER_LEASING_URL can't be resolved (standalone preview without a token).
const ANDYS_LEASING_URL = 'https://andys-motors.com/leasing/';

// `view` of the landing's analytics events — the marker's `data-view` (#1482).
const LANDING_VIEW = 'buy-rent-lease';

// Reserve открывает ДИЛЕРСКИЙ form-template `leasing_booking` (код и сборка
// attrs — в `leaseReserveAttrs.js`): тот же, что стоит в маркере на страницах
// лизинга Andys, поэтому поля/копия — настроенные Антоном (#1227/#1228), а
// CRM-source «Leasing Enquiry», morph формы в чат и MODX-post достаются
// бесплатно, без собственной формы (#1256).

/**
 * AndysLandingConnected — контейнер, связывающий презентационный
 * `AndysBuyRentLease` с реальными side-effects (#1108):
 *  - carCount ← живой инвентарь (useCars);
 *  - popularCars ← он же: «Popular now» на Buy (#1469);
 *  - onOpenCatalog → витрина дилера на сайте Andys (DEALER_CATALOG_URL,
 *    напр. andys-motors.com/used-cars), НЕ demo `/cars`;
 *  - onOpenRental → движок Wheelsystems с нашими `sg_*` параметрами (их
 *    подхватит config-driven prefill bridge на `/car-rental`). CRM-лида у
 *    поиска аренды нет: в нём нет контакта, API отклонял каждый (#1469);
 *    лиды лизинга идут через форму `leasing_booking` (Reserve);
 *  - onAskAI → чат Janette через decoupled событие `shiftgears:open`
 *    (провайдер-независимо, работает и в overlay, и в standalone);
 *  - menuItems → меню сайта с origin-aware href'ами (#1469);
 *  - onTrack → клики в analytics beacon (`sg_landing_*`, #1482) с общими
 *    `view` / `tab` / `msSinceReady` (services/landingEvents.js).
 *
 * Рендерится и как widget-view overlay (на сайте дилера), и как preview-
 * маршрут `/andys` на demo-домене.
 *
 * @param {object} props
 * @param {() => void} [props.onRequestClose] — закрыть overlay перед
 *   навигацией / открытием чата (в overlay-режиме). В маршруте не передаётся.
 * @param {(open: boolean) => void} [props.onFormOpenChange] — сообщить overlay,
 *   что открыта/закрыта лизинговая форма: пока она открыта, overlay уступает ей
 *   z-index (модалка живёт в отдельном body-level хосте, #1256). В preview-
 *   маршруте не передаётся.
 */
const pruneUndefined = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

export const AndysLandingConnected = ({ onRequestClose, onFormOpenChange }) => {
  // Лендинг на экране: от этого момента считается `msSinceReady`, пока (и если)
  // маркер главной не пришлёт `sg_landing_ready` (#1482).
  useEffect(() => {
    noteLandingShown({ view: LANDING_VIEW, tab: ANDYS_DEFAULT_TAB });
  }, []);

  const { getAllCars } = useCars();
  const cars = getAllCars();
  const carCount = cars.length > 0 ? cars.length : null;

  // «Popular now» на Buy (#1469): свободные hot offers в порядке каталога
  // (до 10), нет таких — первые 10 свободных. Пока инвентарь грузится, cars
  // пустой → слайдов нет → секция скрыта. Тап по плитке — детальная машины в
  // каталоге дилера, та же база, что у «Browse N cars» (handleOpenCatalog).
  const popularCars = useMemo(
    () => buildPopularCarSlides(cars, config.DEALER_CATALOG_URL || ANDYS_SHOWROOM_URL),
    [cars],
  );

  // Резолвнутый primary дилера (showroomTokenResolver кладёт theme в
  // b2bMountParams при mount по токену Andys → #E31837). Передаём в лендинг,
  // чтобы прод показывал точный брендовый цвет; без токена (preview) лендинг
  // возьмёт Andys preset red, а НЕ ambient demo-orange (#1108).
  const primaryColor =
    (typeof window !== 'undefined' && window.b2bMountParams?.theme?.primaryColor) || null;

  // Lease categories: per-dealer override from widget-config/mount if present,
  // иначе презентационный компонент возьмёт дефолтный хардкод-конфиг (#1108).
  const leaseCategories =
    (typeof window !== 'undefined' && window.b2bMountParams?.leaseCategories) || undefined;

  // Dealer logo: resolved widget-config branding logo (absolute demo URL in
  // prod). Fallback MUST be null (not a host-absolute '/assets/...' path): in
  // widget mode DEALER_LOGO_URL is null, and a host-relative path resolves
  // against the dealer origin (e.g. dev.eezyworks.com/assets/...) → 404. Null
  // makes AndysBuyRentLease use the BUNDLED andysLogo (base-correct Vite asset
  // that loads on any origin) (#1108).
  const logoUrl = config.DEALER_LOGO_URL || null;

  // Меню сайта (#1469): href'ы ORIGIN-AWARE, как у Rent/Lease CTA (#1252) —
  // test-лендинг ведёт на test-сайт, prod на prod. Геттеры catalog/rental/
  // leasing уже знают свои страницы; остальные собираются из сайта дилера +
  // WEBSITE_PAGES. Без резолвнутого сайта (standalone preview) — прод-URL.
  // Считаем на каждом рендере (6 строк), не useMemo([]): геттеры не реактивны,
  // и в preview по ?token сайт дилера резолвится ПОСЛЕ первого рендера.
  const menuItems = buildAndysMenuItems({
    siteUrl: config.DEALER_SITE_URL,
    pathPrefix: config.WEBSITE_PATH_PREFIX,
    pages: config.WEBSITE_PAGES,
    catalogUrl: config.DEALER_CATALOG_URL,
    rentalUrl: config.DEALER_RENTAL_URL,
    leasingUrl: config.DEALER_LEASING_URL,
  });
  // Баннер BFC — туда же, на сайт этого окружения.
  const bfcUrl = buildAndysBfcUrl({
    siteUrl: config.DEALER_SITE_URL,
    pathPrefix: config.WEBSITE_PATH_PREFIX,
    pages: config.WEBSITE_PAGES,
  });
  // Условия аренды под формой Rent — страница аренды этого окружения, та же,
  // куда уводит «Check availability» (#1469).
  const rentalTermsUrl = config.DEALER_RENTAL_URL || ANDYS_RENTAL_ENGINE_URL;

  // Buy → the dealer's car showroom (витрина) ON THE ANDYS SITE, never the demo
  // /cars route. DEALER_CATALOG_URL resolves the embedded showroom page on the
  // dealer origin (e.g. andys-motors.com/used-cars); fall back to the Andys
  // used-cars page so even the standalone preview leaves for andys-motors.com.
  // The search query rides the widget HashRoute so the showroom can filter —
  // same deep-link shape as the mini-car QR target (#1108).
  const handleOpenCatalog = (query) => {
    if (typeof window === 'undefined') return;
    onRequestClose?.();
    const base = config.DEALER_CATALOG_URL || ANDYS_SHOWROOM_URL;
    const q = (query || '').trim();
    window.location.href = q
      ? `${base}#/cars?search=${encodeURIComponent(q)}`
      : base;
  };

  // Ask AI → open the Janette chat widget via the decoupled `shiftgears:open`
  // event (same channel MiniCarModal / NewModelLanding use). NB: мы НЕ закрываем
  // лендинг (раньше звали onRequestClose → overlay размонтировался и юзер падал
  // в каталог под ним, #1240). Overlay остаётся смонтированным и уступает чату
  // z-index'ом, пока чат открыт (см. AndysLandingOverlay).
  const handleAskAI = (ctx) => {
    if (typeof window === 'undefined') return;
    // Прокидываем текущий контекст лендинга в чат (tab + поисковый запрос Buy /
    // выбранная категория Lease), чтобы Janette продолжила с того же места.
    const launchContext = pruneUndefined({
      source: 'andys-landing',
      tab: ctx?.tab,
      query: ctx?.query || undefined,
      category: ctx?.category?.label,
    });
    window.dispatchEvent(
      new CustomEvent('shiftgears:open', {
        detail: { mode: 'chat', launchContext },
      }),
    );
  };

  const handleOpenRental = (params) => {
    if (typeof window === 'undefined') return;
    // Wheelsystems игнорирует URL-параметры (доказано, #1108) — эти `sg_*`
    // читает наш config-driven prefill bridge на `/car-rental`, не движок.
    // ORIGIN-AWARE target (#1252): ведём на `/car-rental` ТОГО ЖЕ окружения,
    // где открыт лендинг (test→test, prod→prod), иначе с test-лендинга уходили
    // на прод-`/car-rental` с прод-конфигом (флаг off) → prefill не срабатывал.
    // Фолбэк на хардкод-прод — для standalone/preview без резолвнутого сайта.
    const url = new URL(config.DEALER_RENTAL_URL || ANDYS_RENTAL_ENGINE_URL);
    // Пробрасываем весь выбор клиента (локация, даты, возраст, one-way) в `sg_*`.
    // Wheelsystems сам query-string НЕ читает — их применяет наш prefill bridge
    // на `/car-rental` (rentPrefillBridge.js) через DOM движка (#1240).
    if (params.locationCode) url.searchParams.set('sg_loc', params.locationCode);
    // Человекочитаемое название точки — им bridge типизирует free-text
    // autocomplete движка (код SHR/LCA туда не подходит), #1240.
    if (params.locationLabel) url.searchParams.set('sg_loc_label', params.locationLabel);
    if (params.from) url.searchParams.set('sg_from', params.from);
    if (params.to) url.searchParams.set('sg_to', params.to);
    // Время выдачи/возврата — от него зависит цена (#1469).
    if (params.fromTime) url.searchParams.set('sg_from_time', params.fromTime);
    if (params.toTime) url.searchParams.set('sg_to_time', params.toTime);
    if (params.age) url.searchParams.set('sg_age', params.age);
    // Купон — в поле Coupon движка (#cdp_coupon), #1469.
    if (params.coupon) url.searchParams.set('sg_coupon', params.coupon);
    if (params.oneWay) {
      url.searchParams.set('sg_oneway', '1');
      // Точка возврата: код — для selectLocation() движка, название — фолбэк.
      if (params.dropoffCode) url.searchParams.set('sg_drop', params.dropoffCode);
      if (params.dropoffLabel) url.searchParams.set('sg_drop_label', params.dropoffLabel);
    }
    window.location.href = url.toString();
  };

  // «Browse all» → страница лизинга дилера со всеми вариантами. ORIGIN-AWARE
  // (#1256): с test-лендинга ведём на test `/leasing/`, а не на прод — ровно тот
  // баг, что ловили на rental в #1252. Фильтр по типу НЕ пробрасываем: у
  // дилерской /leasing/ все машины одним списком, тип зашит только в slug
  // детальной страницы — матчинг невозможен без доработки на стороне дилера
  // (#1248, решение в issue).
  const handleOpenLease = () => {
    if (typeof window === 'undefined') return;
    onRequestClose?.();
    window.location.href = config.DEALER_LEASING_URL || ANDYS_LEASING_URL;
  };

  // «Reserve» на выбранной категории → дилерская форма лизинговой заявки
  // (form-template `leasing_booking`) модалкой поверх лендинга. Лендинг НЕ
  // закрываем: форма — часть этого экрана, после submit она сама морфится в чат.
  const [reserveCategory, setReserveCategory] = useState(null);
  const handleReserveLease = useCallback(
    (category) => {
      if (!category) return;
      setReserveCategory(category);
      // Overlay должен уступить модалке z-index (см. onFormOpenChange).
      onFormOpenChange?.(true);
    },
    [onFormOpenChange],
  );
  const handleCloseReserve = useCallback(() => {
    setReserveCategory(null);
    onFormOpenChange?.(false);
  }, [onFormOpenChange]);

  // Attrs дилерского темплейта — чистая `buildLeaseReserveAttrs` (покрыта
  // leaseReserveAttrs.test.js): именно они решают, с какими полями лид уедет в
  // CRM, поэтому логика вынесена из компонента и тестируется отдельно (#1256).
  const reserveAttrs = useMemo(() => buildLeaseReserveAttrs(reserveCategory), [reserveCategory]);

  return (
    <>
      <AndysBuyRentLease
        carCount={carCount}
        popularCars={popularCars}
        primaryColor={primaryColor}
        leaseCategories={leaseCategories}
        logoUrl={logoUrl}
        menuItems={menuItems}
        bfcUrl={bfcUrl}
        rentalTermsUrl={rentalTermsUrl}
        onOpenCatalog={handleOpenCatalog}
        onOpenRental={handleOpenRental}
        onOpenLease={handleOpenLease}
        onReserveLease={handleReserveLease}
        onAskAI={handleAskAI}
        onTrack={trackLandingEvent}
      />
      {reserveAttrs && <LeadFormEmbed attrs={reserveAttrs} onClose={handleCloseReserve} />}
    </>
  );
};

export default AndysLandingConnected;
